/**
 * Maps the service's proposal (a FitResult plus field provenance, test-plan §1a) onto field
 * records and a design (plan §4.3 classify-and-fit). Published facts come from the pasted text
 * and are quoted; image readings are `proposed`; anything the analysis could not read is
 * `unresolved` (never invented). No field is ever confirmed here.
 */
import type { Design, FieldRecord, Fields, ProposalWithFields } from '@/domain';
import { CRITICAL_FIELDS, FIELD_LABELS, fieldsFromProposal, fmtMm, repairsFieldFor, STOCK } from '@/domain';
import { designFromProposal } from '@/families/fit';
import type { FitCandidate } from '@/families/fit-schema';
import { resolveStock, type AnyStock } from '@/families/stock';

const ALIASES: Record<string, string[]> = {
  W: ['w', 'width', 'overallwidth', 'externalwidth'],
  H: ['h', 'height', 'overallheight', 'externalheight'],
  D: ['d', 'depth', 'overalldepth', 'externaldepth'],
  stockThickness: ['stockthickness', 'thickness', 'boardthickness', 'panelthickness', 'plywoodthickness', 'materialthickness'],
  shelfCount: ['shelfcount', 'shelves', 'numberofshelves'],
  material: ['material', 'carcassmaterial'],
  finish: ['finish', 'surfacefinish'],
};
export function fieldKeyOf(kind: string): string | null {
  const k = kind.toLowerCase().replace(/[^a-z]/g, '');
  for (const [key, list] of Object.entries(ALIASES)) if (list.includes(k)) return key;
  return null;
}

export interface MappedProposal {
  proposal: ProposalWithFields;
  design: Design;
  fields: Fields;
  /** Photo-versus-design strips: proposed opening ratios of bay 1, bottom-up. */
  ratios: number[];
}

/**
 * Field records and a design from the service's field provenance. `fallback` supplies the
 * starting size when the text publishes none (marked unresolved, so Check asks for it).
 */
export function mapProposal(pwf: ProposalWithFields, { createdAt = new Date().toISOString(), fallback, stock = STOCK }: { createdAt?: string; fallback: Design; stock?: AnyStock }): MappedProposal {
  const { design, unresolved } = designFromProposal(pwf, stock, { W: fallback.W / 10, H: fallback.H / 10, D: fallback.D / 10 });
  const r = resolveStock(stock);
  const fields: ProposalWithFields['fields'] = { ...pwf.fields };
  const missing = [...unresolved];
  for (const key of ['W', 'H', 'D'] as const) {
    if (!fields[key]) fields[key] = { value: design[key] / 10, display: `${fmtMm(design[key])} mm`, status: 'proposed', source: 'image' };
  }
  if (!fields.shelfCount) { fields.shelfCount = { value: design.n, display: `${design.n} shelves`, status: 'proposed', source: 'image' }; missing.push('shelfCount'); }
  if (!fields.stockThickness) { fields.stockThickness = { value: r.thicknessTicks / 10, display: `${fmtMm(r.thicknessTicks)} mm`, status: 'proposed', source: 'image' }; missing.push('stockThickness'); }
  const proposal: ProposalWithFields = { ...pwf, createdAt: pwf.createdAt || createdAt, fields };
  const out = fieldsFromProposal(proposal);
  for (const key of missing) {
    const f = out[key];
    const display = f?.display ?? '';
    const rec: FieldRecord = {
      key, label: FIELD_LABELS[key] ?? key, critical: CRITICAL_FIELDS.includes(key), value: f?.value, display,
      status: 'unresolved', source: 'image', evidence: { note: 'Not found in the photos or the text: give it a value.' }, note: 'not found by the analysis',
      history: [{ status: 'unresolved', value: f?.value, display, at: createdAt, by: 'analysis' }],
    };
    out[key] = rec;
  }
  // plan D5: a repair that changed the photographed layout a lot is held: export waits until the user accepts it or edits the layout
  const repairs = repairsFieldFor(design, { at: createdAt });
  if (repairs) out.repairs = repairs;
  const ratios = Array.isArray(fields.openingRatios?.value) ? (fields.openingRatios!.value as number[]) : [];
  return { proposal, design, fields: out, ratios };
}

export interface Refusal { reason: string; detected?: { family: string; bays: number }; candidates?: FitCandidate[] }

/** Normalise a refusal (no-fit) from the SSE `refusal` event. */
export function refusalFrom(p: Refusal): Refusal {
  return { reason: p.reason || 'We can’t rebuild this one yet.', ...(p.detected ? { detected: p.detected } : {}), ...(p.candidates ? { candidates: p.candidates } : {}) };
}
